import { format, isThisYear, isToday, isYesterday } from 'date-fns';
import type { InboxMailFolder, InboxMessage, InboxQuote, InboxReaction } from '@/lib/inbox/types';

const QUOTE_KIND: Record<string, string> = {
  image: 'Photo',
  sticker: 'Sticker',
  video: 'Video',
  short: 'Video',
  gif: 'GIF',
  audio: 'Audio',
  voice: 'Voice message',
  document: 'Document',
  link_preview: 'Link',
};

function digits(value: string) {
  return value.replace(/\D/g, '');
}

function quotedBody(message: Pick<InboxMessage, 'body_text' | 'attachments'>) {
  const body = message.body_text.trim();
  if (body) return body;
  const file = message.attachments[0];
  if (!file) return '';
  if (file.kind === 'document' && file.name) return file.name;
  return QUOTE_KIND[file.kind || ''] || file.name;
}

function quoteAuthor(quote: InboxQuote, original: InboxMessage | undefined, me: string) {
  const mine = digits(quote.author) && digits(quote.author) === digits(me);
  if (original?.direction === 'outbound' || (!original && mine)) return 'You';
  if (original) return original.sender_name.trim() || formatPhone(original.sender_handle) || 'Someone';
  return formatPhone(quote.author) || 'Someone';
}

const QUOTE_VISUAL = new Set(['image', 'sticker', 'gif']);

function quoteImage(quote: InboxQuote, original: InboxMessage | undefined) {
  const file = original?.attachments.find((item) => item.url && item.kind && QUOTE_VISUAL.has(item.kind));
  return file?.url || quote.image || '';
}

export function quotePreview(quote: InboxQuote, original: InboxMessage | undefined, me = '') {
  const image = quoteImage(quote, original);
  const label = QUOTE_KIND[quote.kind];
  const caption = ((original && quotedBody(original)) || quote.text.trim() || label || '').replace(/\s+/g, ' ');
  const text = image && label && caption === label ? '' : caption;
  return { id: quote.id, author: quoteAuthor(quote, original, me), text, ...(image ? { image } : {}), ...(quote.conversation ? { conversation: quote.conversation } : {}) };
}

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

export type LinkPart = { text: string; href?: string; mention?: boolean };

const MENTION = /@(\d{8,16})(?!\d)/g;

export function mentionDirectory(messages: { direction: string; sender_name: string; sender_handle: string }[], me = '') {
  const names = new Map<string, string>();
  for (const message of messages) {
    const id = message.sender_handle.replace(/\D/g, '');
    if (id.length < 8) continue;
    const named = message.sender_name.trim();
    if (message.direction === 'outbound') names.set(id, named || 'You');
    else if (named && !names.has(id)) names.set(id, named);
  }
  const mine = me.replace(/\D/g, '');
  if (mine.length >= 8 && !names.has(mine)) names.set(mine, 'You');
  return names;
}

export function mentionParts(text: string, names: ReadonlyMap<string, string>): LinkPart[] {
  if (!names.size || !text.includes('@')) return [{ text }];
  const parts: LinkPart[] = [];
  let last = 0;
  let replaced = false;
  for (const match of text.matchAll(MENTION)) {
    const name = names.get(match[1]);
    const start = match.index ?? 0;
    if (!name) continue;
    replaced = true;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: `@${name}`, mention: true });
    last = start + match[0].length;
  }
  if (!replaced) return [{ text }];
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

const EXCERPT_LEAD = 20;
const EXCERPT_LENGTH = 140;

/** The part of a message around a search term, on one line, so the term still shows on a narrow list row. */
export function searchExcerpt(text: string, term: string) {
  const line = text.replace(/\s+/g, ' ').trim();
  const start = Math.max(0, line.toLowerCase().indexOf(term.trim().toLowerCase()) - EXCERPT_LEAD);
  const excerpt = line.slice(start, start + EXCERPT_LENGTH);
  return start ? `…${excerpt}` : excerpt;
}

/** Splits text around each case-insensitive match of a chat search, so the bubble can highlight it. */
export function markParts(text: string, term: string) {
  const needle = term.trim().toLowerCase();
  const lower = text.toLowerCase();
  if (!needle || lower.length !== text.length) return [{ text, hit: false }];
  const parts: { text: string; hit: boolean }[] = [];
  let last = 0;
  for (let at = lower.indexOf(needle); at >= 0; at = lower.indexOf(needle, last)) {
    if (at > last) parts.push({ text: text.slice(last, at), hit: false });
    parts.push({ text: text.slice(at, at + needle.length), hit: true });
    last = at + needle.length;
  }
  if (last < text.length || !parts.length) parts.push({ text: text.slice(last), hit: false });
  return parts;
}

export function messageParts(text: string, names: ReadonlyMap<string, string>) {
  return mentionParts(text, names).flatMap((part) => (part.mention ? [part] : linkParts(part.text)));
}

function trimUrl(raw: string) {
  let end = raw.length;
  while (end > 0 && /[.,;:!?]/.test(raw[end - 1])) end -= 1;
  while (end > 0 && raw[end - 1] === ')') {
    const slice = raw.slice(0, end);
    const open = slice.match(/\(/g)?.length || 0;
    const close = slice.match(/\)/g)?.length || 0;
    if (close <= open) break;
    end -= 1;
  }
  return raw.slice(0, end);
}

function safeHref(value: string) {
  try {
    const url = new URL(value.startsWith('www.') ? `https://${value}` : value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    return url.href;
  } catch {
    return '';
  }
}

export function linkParts(text: string): LinkPart[] {
  const parts: LinkPart[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_PATTERN)) {
    const start = match.index ?? 0;
    const raw = match[0];
    const trimmed = trimUrl(raw);
    const href = safeHref(trimmed);
    if (!href || start < last) continue;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: trimmed, href });
    last = start + trimmed.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts.length ? parts : [{ text }];
}

export function formatPhone(value: string) {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  if (digits.length === 10) return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return digits.length >= 8 ? `+${digits}` : value;
}

export function chatDay(iso: string) {
  const date = new Date(iso);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, isThisYear(date) ? 'EEEE, MMM d' : 'MMM d, yyyy');
}

export function chatTime(iso: string) {
  return format(new Date(iso), 'h:mm a');
}

export function initials(name: string) {
  const words = name
    .replace(/<[^>]*>|["@].*$/g, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[words.length - 1][0] : (words[0] ?? '?').slice(0, 2);
  return letters.toUpperCase();
}

export function avatarTone(seed: string, dark = false) {
  let hash = 0;
  for (const char of seed.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  const hue = hash % 360;
  return dark ? { bg: `hsl(${hue} 22% 24%)`, fg: `hsl(${hue} 36% 82%)` } : { bg: `hsl(${hue} 38% 92%)`, fg: `hsl(${hue} 32% 28%)` };
}

/** The reply reason of a conversation whose last message is ours. Mail rows show it as "You:" before the preview. */
export const SENT_LAST_REASON = 'You sent the last message';

export function isHouseEmail(email?: string | null) {
  const domain = email?.trim().toLowerCase().split('@')[1];
  return Boolean(domain && (domain === 'thetimelesscollector.com' || domain.endsWith('.thetimelesscollector.com')));
}

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;

export function attachmentLimitError(sizes: number[]) {
  if (sizes.length > MAX_ATTACHMENTS) return `Attach up to ${MAX_ATTACHMENTS} files`;
  if (sizes.reduce((total, size) => total + size, 0) > MAX_ATTACHMENT_BYTES) return 'Attachments are over 25 MB';
  return '';
}

export function fileSize(bytes = 0) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The mailbox to show when opening a channel. A remembered choice wins, including All. Otherwise the starred mailbox. */
export function accountForSheet(accounts: { id: string; is_default: boolean }[], remembered?: string) {
  if (remembered !== undefined && (remembered === '' || accounts.some((account) => account.id === remembered))) return remembered;
  return accounts.find((account) => account.is_default)?.id ?? '';
}

export function mailboxName(label: string) {
  if (!label.includes('@')) return label;
  const words = label
    .split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean);
  if (!words.length) return label;
  return words.map((word) => word[0].toUpperCase() + word.slice(1)).join(' ');
}

const CHAT_CHANNELS = new Set(['whatsapp', 'messenger']);

/** WhatsApp and Messenger: chat layout, instant sending, swipe to mark handled. */
export function isChatChannel(channel: string | undefined) {
  return CHAT_CHANNELS.has(channel || '');
}

export function chatChannelName(channel: string | undefined) {
  return channel === 'messenger' ? 'Messenger' : 'WhatsApp';
}

/** The handle shown under a chat's name. WhatsApp shows the phone; a Messenger handle is a Facebook id, so nothing. */
export function chatHandle(channel: string | undefined, handle: string) {
  return channel === 'whatsapp' ? formatPhone(handle) : '';
}

export function inboxChannelFromPath(pathname: string) {
  const sheet = pathname.split('/')[2];
  return isChatChannel(sheet) ? sheet : 'gmail';
}

export function inboxSheetPath(channel: string) {
  return isChatChannel(channel) ? `/inbox/${channel}` : '/inbox/mail';
}

export function inboxAllLabel(channel: string) {
  return isChatChannel(channel) ? 'All chats' : 'All mail';
}

export function inboxSheetChannel(sheet: string) {
  if (isChatChannel(sheet)) return sheet;
  if (sheet === 'mail') return 'gmail';
  return '';
}

function inboxParts(pathname: string) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'inbox' || !inboxSheetChannel(parts[1] || '') || parts.length > 4) return [];
  return parts;
}

export function inboxConversationFromPath(pathname: string) {
  const parts = inboxParts(pathname);
  return parts[2] ? decodeURIComponent(parts[2]) : '';
}

export function inboxMessageFromPath(pathname: string) {
  const parts = inboxParts(pathname);
  return parts[3] ? decodeURIComponent(parts[3]) : '';
}

export function inboxConversationPath(channel: string, id = '', messageId = '') {
  const sheet = inboxSheetPath(channel);
  if (!id) return sheet;
  const conversation = `${sheet}/${encodeURIComponent(id)}`;
  return messageId ? `${conversation}/${encodeURIComponent(messageId)}` : conversation;
}

export function inboxLocationMatches(pathname: string, channel: string, id = '', messageId = '') {
  const sheet = isChatChannel(channel) ? channel : 'gmail';
  return inboxChannelFromPath(pathname) === sheet && inboxConversationFromPath(pathname) === id && inboxMessageFromPath(pathname) === messageId;
}

/** Split a WhatsApp list snippet (`Alex: …` / `You: …`) into sender and text. */
export function chatPreview(snippet: string) {
  const match = snippet.match(/^(You|\+?\d{6,15}|[^\s:]{1,24}):\s([\s\S]+)$/);
  if (!match) return { from: '', text: snippet };
  return { from: match[1], text: match[2] };
}

export function listTime(iso: string) {
  const date = new Date(iso);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isThisYear(date)) return format(date, 'MMM d');
  return format(date, 'MMM d, yyyy');
}

export function messageTime(iso: string) {
  const date = new Date(iso);
  return format(date, isThisYear(date) ? 'EEE, MMM d, h:mm a' : 'MMM d, yyyy, h:mm a');
}

export function splitQuoted(text: string) {
  const lines = text.split('\n');
  const start = lines.findIndex((line, index) => /^On .+wrote:$/.test(line.trim()) || (line.startsWith('>') && lines.slice(index).every((rest) => rest.startsWith('>') || !rest.trim())));
  if (start <= 0) return { main: text.trimEnd(), quoted: '' };
  return { main: lines.slice(0, start).join('\n').trimEnd(), quoted: lines.slice(start).join('\n').trim() };
}

/** The pill under a chat bubble: up to three emojis, most used first, and how many reacted. */
export function reactionSummary(reactions: InboxReaction[]) {
  const counts = new Map<string, number>();
  for (const reaction of reactions) counts.set(reaction.emoji, (counts.get(reaction.emoji) ?? 0) + 1);
  const emojis = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([emoji]) => emoji);
  return { emojis: emojis.slice(0, 3), total: reactions.length };
}

/** The reactions with the account's own one swapped for `mine` while it is being sent; '' removes it. */
export function reactionsWithMine(reactions: InboxReaction[], mine: string | undefined) {
  if (mine === undefined) return reactions;
  const others = reactions.filter((reaction) => !reaction.mine);
  return mine ? [...others, { emoji: mine, name: '', handle: '', mine: true }] : others;
}

export const DELETED_TEXT = 'This message was deleted';

/** WhatsApp lets the sender delete a message for everyone for about two and a half days after sending it. */
export const DELETE_WINDOW_MS = 60 * 60 * 60_000;

export function canDeleteForEveryone(message: Pick<InboxMessage, 'direction' | 'sent_at' | 'deleted'>, now = Date.now()) {
  return message.direction === 'outbound' && !message.deleted && now - new Date(message.sent_at).getTime() < DELETE_WINDOW_MS;
}

/** "Fwd:" once, as Gmail does. Shared by the forward sheet and the email it sends. */
export function forwardSubject(subject: string) {
  const clean = subject.trim();
  return /^(fwd?|fw):/i.test(clean) ? clean : `Fwd: ${clean}`;
}

/** Gmail's folders beside its labels, each with the Gmail search that lists it. */
export const MAIL_FOLDERS: Record<InboxMailFolder, { label: string; query: string }> = {
  starred: { label: 'Starred', query: 'is:starred' },
  sent: { label: 'Sent', query: 'in:sent' },
};

export function mailFolder(value: string | null | undefined): InboxMailFolder | undefined {
  return value === 'sent' || value === 'starred' ? value : undefined;
}
